/* eslint-disable @typescript-eslint/default-param-last */
import * as React from "react";

export type NotificationType = {
    id: string;
    type: "normal" | "error" | "success";
    title: React.ReactNode;
    description: string;
    duration?: number;
};
export type AddNotificationHandler = (
    type: NotificationType["type"],
    title: NotificationType["title"],
    decription?: NotificationType["description"],
    duration?: NotificationType["duration"],
) => NotificationType;
export type UpdateNotificationHandler = (
    notificationID: NotificationType["id"],
    type: NotificationType["type"],
    title: NotificationType["title"],
    description?: NotificationType["description"],
    duration?: NotificationType["duration"],
) => void;
type ContextType = {
    notifications: NotificationType[];
    addNotification: AddNotificationHandler;
    updateNotification: UpdateNotificationHandler;
    removeNotification(notificationId: NotificationType["id"]): void;
};

const NotificationsContext = React.createContext<null | ContextType>(null);
const useNotifications = (): ContextType => {
    const context = React.useContext(NotificationsContext);
    if (context === null) {
        throw new Error("Missing NotificationsProvider at root");
    }
    return context;
};

type Props = {
    children: React.ReactNode;
};

const NotificationsProvider: React.FC<Props> = ({ children }) => {
    const [notifications, setNotifications] = React.useState<
        NotificationType[]
    >([]);
    const addNotification: ContextType["addNotification"] = React.useCallback(
        (type = "normal", title, description = "", duration = 3000) => {
            const notification = {
                id: `${Math.floor(Math.random() * 1e10)}`,
                type,
                title,
                description,
                duration,
            };
            setNotifications((prev: any) => prev.concat(notification));
            // TODO Fix any
            return notification;
        },
        [],
    );
    const updateNotification: ContextType["updateNotification"] =
        React.useCallback(
            (notificationID, type, title, description, duration = 3000) => {
                setNotifications((prevNotifications) => {
                    const newNotifications = [...prevNotifications];
                    const index = newNotifications.findIndex(
                        (x) => x.id === notificationID,
                    );
                    if (index === -1) {
                        // notification id not found
                        addNotification(type, title, description, duration);
                        return [...prevNotifications];
                    }
                    const notification = prevNotifications[index];
                    notification.type = type;
                    notification.title = title;
                    notification.description = description || "";
                    notification.duration = duration;
                    newNotifications[index] = notification;
                    return newNotifications;
                });
            },
            [],
        );
    const removeNotification: ContextType["removeNotification"] =
        React.useCallback((notificationId) => {
            setNotifications((prev) =>
                prev.filter(
                    (notification) => notification.id !== notificationId,
                ),
            );
        }, []);
    const context: ContextType = React.useMemo(
        () => ({
            notifications,
            addNotification,
            updateNotification,
            removeNotification,
        }),
        [
            notifications,
            addNotification,
            updateNotification,
            removeNotification,
        ],
    );

    return (
        <NotificationsContext.Provider value={context}>
            {children}
        </NotificationsContext.Provider>
    );
};

export { useNotifications, NotificationsProvider };
